import { useMemo } from "react";
import { useTheme } from "@mui/material";
import classes from "./KeikkaPumpIcon.module.css";

const ASSET_BASE = "/img/keikkaIcons2/";
const MAX_M3 = 50;
const MAX_LINJA = 85;

// For the no-boom pipe-chain, some S-piece indices are rendered using the
// B-piece file (the same shape, mirrored or repositioned). Only S0, S1, S5,
// S6, S7 exist as actual PNGs in /img/keikkaIcons2/BS/ — every other index
// maps to a B file. Kept identical to KeikkaIcon2 to preserve visual output.
const S_TO_B_MAP = {
    2: "BS/B5.png",
    3: "BS/B4.png",
    4: "BS/B3.png",
    8: "BS/B12.png",
    9: "BS/B11.png",
    10: "BS/B10.png",
    11: "BS/B9.png",
    12: "BS/B8.png",
};

function buildLayers({ m3, puomi, linja, isPumi }) {
    const totm3 = Number(m3) || 0;
    const boom = Number(puomi) || 0;
    const line = Number(linja) || 0;

    const files = [];
    const fills = [];

    if (isPumi) {
        if (boom > 0) {
            files.push("PB.png");
            files.push("BS/B1.png");
            const fillBucket = Math.min(Math.round((totm3 * 10) / MAX_M3), 10);
            if (fillBucket > 0) files.push(`PBF/PBF${fillBucket}.png`);
        } else {
            files.push("PNB.png");
            const fillBucket = Math.min(Math.round((totm3 * 10) / MAX_M3), 10);
            if (fillBucket > 0) fills.push(`PBF/PBF${fillBucket}.png`);
        }
    } else {
        const bigPump = totm3 > 40;
        if (bigPump) {
            files.push(boom > 0 ? "BPB.png" : "BPNB.png");
        } else {
            files.push(boom > 0 ? "SPB.png" : "SPNB.png");
        }
        if (boom > 0) files.push("BS/B1.png");
    }

    // Pipe-chain pieces (linja). Selection depends only on boom presence,
    // not on vehicle type — both pumi and pumppu use the same chain.
    if (boom > 0) {
        const maxNum = Math.min((line * 12) / MAX_LINJA + 1, 13);
        for (let i = 2; i <= maxNum; i++) {
            files.push(`BS/B${i}.png`);
        }
    } else {
        const maxNum = Math.min((line * 11) / MAX_LINJA, 11);
        for (let i = 1; i <= maxNum; i++) {
            if (i === 1) files.push("BS/S0.png");
            files.push(S_TO_B_MAP[i] || `BS/S${i}.png`);
        }
    }

    return { files, fills };
}

export default function KeikkaPumpIcon({
    m3,
    puomi,
    linjanPituus,
    isPumi,
    size = 40,
    noAnimation = false,
    mirror = false,
    opacity = 0.85,
    invertMode,
}) {
    const theme = useTheme();
    const effectiveMode = invertMode ?? theme.palette.mode;
    const inversePercent = effectiveMode === "dark" ? "80%" : "20%";

    const { files, fills } = useMemo(
        () => buildLayers({ m3, puomi, linja: linjanPituus, isPumi }),
        [m3, puomi, linjanPituus, isPumi],
    );

    const containerStyle = {
        opacity,
        filter: `invert(${inversePercent})`,
        width: size,
        height: size,
        flexShrink: 0,
        transform: mirror ? "scaleX(-1)" : "none",
    };

    function renderLayers(list, baseDelay, step, wrapperClass) {
        return list.map((f, index) => (
            <div key={f} className={wrapperClass}>
                <img
                    src={`${ASSET_BASE}${f}`}
                    alt=""
                    className={classes.icon}
                    style={noAnimation
                        ? { animation: "none", opacity: 1 }
                        : { animationDelay: `${baseDelay + index * step}ms` }
                    }
                />
            </div>
        ));
    }

    return (
        <div className={classes.div} style={containerStyle}>
            {renderLayers(files, 100, 120, undefined)}
            {renderLayers(fills, 300, 100, classes.fillsDiv)}
        </div>
    );
}
